if (!customElements.get("video-with-play")) {
  class VideoWithPlay extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.videoInfo = this.querySelector(".video-with-play__info");
      this.playButton = this.querySelector(".video-with-play__play");
      this.video = this.querySelector("video");

      this.addEventListener("click", (event) => {
        event.stopPropagation();

        if (!this.video) return console.error("Video not found.");

        if (this.video?.paused) {
          this.video?.play();

          this.videoInfo && (this.videoInfo.style.visibility = "hidden");

          this.playButton && (this.playButton.style.visibility = "hidden");
        } else {
          this.video?.pause();

          this.videoInfo && (this.videoInfo.style.visibility = "visible");

          this.playButton && (this.playButton.style.visibility = "visible");
        }
      });
    }
  }

  customElements.define("video-with-play", VideoWithPlay);
}

class SwiperConfigurator {
  static swiperInstances = new Map();

  constructor(sliderElement) {
    this.element = sliderElement;
    this.options = this.parseConfig();
    this.setupEventListeners();

    if (this.options.observer) {
      this.initIntersectionObserver();
    } else {
      this.initializeSwiper();
    }
  }

  parseConfig() {
    const {
      dataset: { config, mobile, observer },
    } = this.element;

    try {
      return {
        config: JSON.parse(config),
        mobile: mobile === "true",
        observer: observer === "true",
      };
    } catch (error) {
      console.error("Error parsing slider configuration:", error);
      return { config: {}, mobile: false, observer: false };
    }
  }

  initializeSwiper() {
    if (this.shouldDisableSwiper()) {
      return;
    }
    if (!Swiper || !this.element || this.element.swiper) {
      return;
    }
    if (this.options.config.thumbs?.swiper) {
      const thumbsSelector = this.options.config.thumbs.swiper;
      const thumbsElement = document.querySelector(thumbsSelector);
      const thumbsInstance =
        SwiperConfigurator.swiperInstances.get(thumbsElement);

      if (!thumbsInstance) {
        setTimeout(() => this.initializeSwiper(), 0);
        return;
      }

      this.options.config.thumbs.swiper = thumbsInstance;
    }

    const instance = new Swiper(this.element, this.options.config);
    SwiperConfigurator.swiperInstances.set(this.element, instance);
  }

  shouldDisableSwiper() {
    return this.options.mobile && window.innerWidth > 768;
  }

  setupEventListeners() {
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  handleResize() {
    if (this.shouldDisableSwiper()) {
      this.destroySwiper();
    } else {
      this.initializeSwiper();
    }
  }

  destroySwiper() {
    if (this.element && this.element.swiper) {
      this.element.swiper.destroy(true, true);
    }
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            console.log("Element is in view, initializing swiper");
            this.observer.disconnect();
            this.initializeSwiper();
          }
        });
      },
      { threshold: 0.1 }
    );

    this.observer.observe(this.element);
  }

  static appendTagToBody(tagType, filePath) {
    return new Promise((resolve, reject) => {
      const tag = document.createElement(
        tagType === "script" ? "script" : "link"
      );

      if (tagType === "script") {
        tag.src = filePath;
        tag.type = "text/javascript";
      } else if (tagType === "style") {
        tag.href = filePath;
        tag.rel = "stylesheet";
      } else {
        return reject(
          new Error('Unsupported tag type. Please use "script" or "style".')
        );
      }

      tag.onload = resolve;
      tag.onerror = () =>
        reject(new Error(`Failed to load ${tagType} file: ${filePath}`));

      document.body.appendChild(tag);
    });
  }

  static appendSwiperFiles() {
    const { css, js } = window.swiperFile;
    const files = [
      { type: "style", path: css },
      { type: "script", path: js },
    ].filter((file) => file.path);

    if (files.length === 0) {
      return Promise.resolve();
    }

    const loadFilePromises = files.map((file) =>
      SwiperConfigurator.appendTagToBody(file.type, file.path)
    );
    return Promise.all(loadFilePromises);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const sliderElements = document.querySelectorAll("[data-slider-selector]");

  if (sliderElements.length > 0) {
    SwiperConfigurator.appendSwiperFiles()
      .then(() => {
        sliderElements.forEach((slider) => new SwiperConfigurator(slider));
      })
      .catch((error) => {
        console.error("Failed to load Swiper files:", error);
      });
  }
});

if (!customElements.get("sticky-bar")) {
  class StickyBar extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.elements = [];
      this.visibleElements = new Set();
    }

    connectedCallback() {
      this.initObserver();
    }

    initObserver() {
      const selectors =
        this.dataset.observableElements?.split("\n").filter(Boolean) || [];

      this.elements = selectors
        .map((selector) => document.querySelector(selector.trim()))
        .filter(Boolean);

      if (this.elements.length === 0) return this.classList.add('sticky-bar--visible')

      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
        threshold: 0.1, 
      });

      this.elements.forEach((item) => this.observer.observe(item));
    }

    handleIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.visibleElements.add(entry.target);
        } else {
          this.visibleElements.delete(entry.target);
        }
      });

      const isAllHidden = this.visibleElements.size === 0;

      this.classList.toggle("sticky-bar--visible", isAllHidden);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define("sticky-bar", StickyBar);
}

if (!customElements.get('express-form')) {
  class ExpressForm extends HTMLElement {
    constructor() {
      super();

      this.hasGifts = this.hasAttribute('data-has-gifts');
      this.form = this.querySelector('form');
      this.isSubmitProcessed = false;
      this.submitButton = this.form?.querySelector('button[type="submit"]');
    }

    connectedCallback() {
      this.form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        
        if (this.isSubmitProcessed) return;
        this.isSubmitProcessed = true;

        this.setLoadingState(true);

        try {
          if (this.hasGifts) {
            await this.clearCart();
          }

          await this.addToCart();
          
          const returnTo = this.form.querySelector('input[name="return_to"]')?.value || '/checkout';
          window.location.href = returnTo;
        } catch (error) {
          console.error('Error adding to cart:', error);
          this.setLoadingState(false);
          this.isSubmitProcessed = false;
        }
      });
    }

    async clearCart() {
      await fetch('/cart/clear.js');
    }

    async addToCart() {
      const formData = new FormData(this.form);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }

      return response.json();
    }

    setLoadingState(isLoading) {
      if (this.submitButton) {
        this.submitButton.disabled = isLoading;
        this.submitButton.classList.toggle('loading', isLoading);
      }
    }
  }

  customElements.define('express-form', ExpressForm)
}

document.addEventListener('DOMContentLoaded', () => {
  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get('show_cart') === 'true') {
    let cartOpened = false;
    const openCartSidebar = () => {
      if (cartOpened) return true;
      const cartDrawer = document.querySelector('[data-js-site-cart-sidebar]');
      if (!cartDrawer) return false;

      if (typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      } else {
        cartDrawer.classList.add('sidebar--opened');
        document.body.classList.add('sidebar-opened');
      }

      cartOpened = true;
      return true;
    };

    if (!openCartSidebar() && window.customElements && window.customElements.whenDefined) {
      customElements.whenDefined('sidebar-drawer').then(openCartSidebar);
    }

    currentUrl.searchParams.delete('show_cart');
    const newSearch = currentUrl.searchParams.toString();
    const cleanQuery = newSearch ? `?${newSearch}` : '';
    const cleanUrl = `${currentUrl.pathname}${cleanQuery}${currentUrl.hash}`;
    window.history.replaceState({}, document.title, cleanUrl);
  }

  const reviewBadges = document.querySelectorAll('.stamped-product-reviews-badge');
  reviewBadges.forEach((badge) => {
    badge.addEventListener('click', (event) => {
      const targetSelector = badge.getAttribute('href') || '#stamped-main-widget';
      const targetElement = document.querySelector(targetSelector);
      if (!targetElement) {
        return;
      }

      event.preventDefault();
      setTimeout(() => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 1000);
    });
  });

});

window.addEventListener('pageshow', function(event) {
  const navType = performance.getEntriesByType('navigation')[0]?.type;
  if (event.persisted || navType === 'back_forward') {
    window.location.reload();
  }
});
class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.timerInterval = null;
    
    const targetTimeString = this.getAttribute('target-time');
    this.targetDate = targetTimeString ? new Date(targetTimeString.replace(/-/g, "/")) : null;
    
    this.hoursElement = this.querySelector('[data-hours]');
    this.minutesElement = this.querySelector('[data-minutes]');
    this.secondsElement = this.querySelector('[data-seconds]');
    
    this.endedText = this.getAttribute('data-ended-text');
  }

  connectedCallback() {
    if (this.targetDate && !isNaN(this.targetDate)) {
      this.startTimer();
    } else {
      console.error('Timer: Invalid or missing target-time attribute.');
    }
  }

  disconnectedCallback() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  _formatTime(number) {
    return number < 10 ? `0${number}` : number;
  }

  _updateElementText(element, value) {
    if (element) {
      element.textContent = this._formatTime(value);
    }
  }

  updateTimer() {
    const timeCurrent = new Date().getTime();
    const timeDifference = this.targetDate.getTime() - timeCurrent;

    if (timeDifference < 0) {
      clearInterval(this.timerInterval);
      this.innerHTML = this.endedText;
      this.setAttribute('data-expired', 'true');
      return;
    }

    const totalHoursInMs = 1000 * 60 * 60;
    const totalMinutesInMs = 1000 * 60;

    const hours = Math.floor(timeDifference / totalHoursInMs);   
    const minutes = Math.floor((timeDifference % totalHoursInMs) / totalMinutesInMs);  
    const seconds = Math.floor((timeDifference % totalMinutesInMs) / 1000);

    this._updateElementText(this.hoursElement, hours)
    this._updateElementText(this.minutesElement, minutes)
    this._updateElementText(this.secondsElement, seconds)
  }

  startTimer() {
    this.updateTimer(); 
    this.timerInterval = setInterval(() => this.updateTimer(), 1000);
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
